import { datadogLogs } from "@datadog/browser-logs";
import { withGlobalSpinner } from "lib/api";
import { useEffect, useState } from "react";
import { getEntitlements } from "./entitlements-api";
import type {
  ActiveBenefit,
  ActiveBenefits,
  Benefits,
  RedeemedBenefit
} from "./types";

type EntitlementsModule = {
  loading: boolean;
  benefitsSavings: Benefits;
  redeemedBenefits: RedeemedBenefit[];
  productSavings: number;
  activeBenefits: ActiveBenefits;
};

const SAMPLES_BENEFIT = "SAMPLES";
const WELCOME_GIFT_BENEFIT = "WELCOME_GIFT";
const ANNIVERSARY_GIFT_BENEFIT = "ANNIVERSARY_GIFT";

function mapActiveBenefits(benefits: ActiveBenefit[]) {
  return {
    samples:
      benefits.find((benefit) => benefit.entitlementName === SAMPLES_BENEFIT) ||
      null,
    welcomeGift:
      benefits.find(
        (benefit) => benefit.entitlementName === WELCOME_GIFT_BENEFIT
      ) || null,
    anniversaryGift:
      benefits.find(
        (benefit) => benefit.entitlementName === ANNIVERSARY_GIFT_BENEFIT
      ) || null
  };
}

export const useEntitlements = (): EntitlementsModule => {
  const [loading, setLoading] = useState(true);
  const [benefitsSavings, setBenefitsSavings] = useState<Benefits>({
    totalBenefitsSavings: 0,
    categories: [{ title: "Benefits Savings", value: 0 }]
  });
  const [redeemedBenefits, setRedeemedBenefits] = useState<RedeemedBenefit[]>(
    []
  );
  const [productSavings, setProductSavings] = useState<number>(0);
  const [activeBenefits, setActiveBenefits] = useState<ActiveBenefits>({
    samples: null,
    anniversaryGift: null,
    welcomeGift: null
  });

  useEffect(() => {
    (async () => {
      try {
        const entitlements = await withGlobalSpinner(getEntitlements)();
        setBenefitsSavings(entitlements.benefitsSavings as Benefits);
        setRedeemedBenefits(entitlements.redeemedBenefits);
        setProductSavings(entitlements.productSavings.totalProductsSavings);
        setActiveBenefits(mapActiveBenefits(entitlements.activeBenefits));
      } catch (error) {
        datadogLogs.logger.error(
          "Failed fetching entitlements",
          error as Error
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return {
    loading,
    benefitsSavings,
    redeemedBenefits,
    productSavings,
    activeBenefits
  };
};
